"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import { AIAnalysis, DailyLog } from "@/types";
import clsx from "clsx";
import { motion } from "framer-motion";
import { Activity, Droplets, Pill, Wine, Cigarette, Sun, Smartphone, Users, Smile, Zap, Moon, Flame, Utensils, Info, Check, AlertCircle, CheckCircle2, Share2 } from "lucide-react";
import { calculateDailyScore, calculateCaloriesBurned, generateFeedback, calculateLifestyleScore, calculateDailyProtein, calculateCoreScore } from "@/utils/scoring";
import { formatDateDisplay, formatDateIndian } from "@/utils/date";

const getSleepHours = (sleep: number | { hours: number; quality?: number }) => {
    return typeof sleep === 'object' ? sleep.hours : sleep;
};

const generateShareText = (log: DailyLog) => {
    const meals = log.meals || [];
    const totalProtein = calculateDailyProtein(log);
    const score = calculateDailyScore(log);
    const lifestyleScore = calculateLifestyleScore(log);
    const feedback = generateFeedback(log, score);
    const burned = calculateCaloriesBurned(log);

    let text = `🧬 *LongevityOS Report* • ${formatDateDisplay(log.date)}\n\n`;

    // Score Header
    text += `🏆 *Daily Score: ${score}/100*\n`;
    text += `💚 *Lifestyle Score: ${lifestyleScore}/30*\n`;
    text += `_"${feedback.weekly_context_comment}"_\n\n`;

    // 1. Performance Stats
    text += `⚡ *Performance*\n`;
    text += `   • Protein: ${Math.round(totalProtein)}g\n`;
    text += `   • Sleep: ${getSleepHours(log.sleep)}h\n`;
    const steps = log.steps || 0;
    text += `   • Steps: ${steps.toLocaleString()}\n`;
    text += `   • Water: ${log.water}L\n`;
    text += `   • Burned: ${burned} kcal\n\n`;

    // 2. Workout Detail
    const workouts = log.workouts || [];
    if (workouts.some(w => w.type !== "Rest")) {
        text += `🏋️ *Workouts*\n`;
        workouts.filter(w => w.type !== "Rest").forEach(w => {
            text += `   • ${w.type}: ${w.duration}m\n`;
            if (w.calories > 0) text += `   • Calories: ${w.calories} kcal\n`;
        });
        text += `\n`;
    }

    // 3. Nutrition Breakdown
    if (meals.some(m => (m.items || []).length > 0)) {
        text += `🥗 *Nutrition*\n`;
        meals.forEach(meal => {
            if ((meal.items || []).length > 0) {
                text += `   *${meal.name}*\n`;
                meal.items.forEach(item => {
                    text += `   • ${item.quantity}x ${item.name} (${item.protein * item.quantity}g P, ${item.calories * item.quantity} kcal)\n`;
                });
            }
        });
        text += `\n`;
    }

    // 4. Supplements
    const supplements = Object.entries(log.supplements || {}).filter(([, taken]) => taken).map(([name]) => name);
    if (supplements.length > 0) {
        text += `💊 *Supplements*\n`;
        text += `   • ${supplements.join(", ")}\n\n`;
    }

    // 5. Recovery & Lifestyle Detail
    text += `🧘 *Recovery & Stats*\n`;
    text += `   • Stress: ${log.stress || 3}/5\n`;
    text += `   • Mood: ${log.mood || '-'}\n`;
    text += `   • Alcohol: ${log.alcohol?.units || 0} ${log.alcohol?.type ? `(${log.alcohol.type})` : ''}\n`;
    text += `   • Smoking: ${log.smoking?.type || 'No'} ${log.smoking?.quantity ? `(${log.smoking.quantity})` : ''}\n`;
    text += `   • Sunlight: ${log.sunlight || 0}m\n`;
    text += `   • Screen Time: ${log.screenTime || 0}h\n`;
    text += `   • Social: ${log.socialConnect || 3}/5\n`;
    if ((log.meditation || 0) > 0) text += `   • Meditation: ${log.meditation}m\n`;
    if (log.screenFreeBeforeBed) text += `   • Screen-free before bed: Yes\n`;
    text += `\n`;

    // 6. AI Analysis (Pros & Cons)
    text += `🤖 *Coach Insights*\n`;
    if (feedback.score_reasons?.positives?.length > 0) {
        text += `   ✅ *Wins:*\n`;
        feedback.score_reasons.positives.forEach(p => text += `     - ${p}\n`);
    }
    if (feedback.score_reasons?.negatives?.length > 0) {
        text += `   ⚠️ *Improvements:*\n`;
        feedback.score_reasons.negatives.forEach(n => text += `     - ${n}\n`);
    }
    if (feedback.tomorrow_actions?.length > 0) {
        text += `   🚀 *Tomorrow's Plan:*\n`;
        feedback.tomorrow_actions.forEach(a => text += `     - ${a}\n`);
    }

    text += `\n`;

    // Footer
    text += `🚀 Logged via #LongevityOS`;

    return text;
};

interface DayScoreProps {
    log: DailyLog;
    analysis: AIAnalysis | null;
    loading: boolean;
}

export default function DayScore({ log, analysis, loading }: DayScoreProps) {
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-text-muted">
                <div className="relative w-20 h-20 mb-4">
                    <div className="absolute inset-0 border-4 border-white/10 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-xs uppercase tracking-wider font-medium animate-pulse">Analyzing your day...</p>
            </div>
        );
    }

    // Memoized stats
    const totalProtein = useMemo(() => calculateDailyProtein(log), [log]);
    const totalCalories = useMemo(() => 
        (log.meals || []).reduce((sum, meal) => 
            sum + (meal.items || []).reduce((s, i) => s + (i.calories || 0) * (i.quantity || 1), 0), 0
        ), [log.meals]);

    const localScore = useMemo(() => calculateDailyScore(log), [log]);
    const localBurned = useMemo(() => calculateCaloriesBurned(log), [log]);

    const displayAnalysis: AIAnalysis = useMemo(() => {
        return analysis || log.aiAnalysis || {
            day_score: localScore,
            calories_burned: localBurned,
            ...generateFeedback(log, localScore)
        };
    }, [analysis, log, localScore, localBurned]);

    const displayScore = displayAnalysis.day_score;

    const handleShare = useCallback(async () => {
        const text = generateShareText(log);
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `LongevityOS Log - ${formatDateDisplay(log.date)}`,
                    text: text
                });
            } else {
                await navigator.clipboard.writeText(text);
                alert("Day Summary copied to clipboard!");
            }
        } catch (err) {
            console.error("Failed to share:", err);
            try {
                await navigator.clipboard.writeText(text);
                alert("Day Summary copied to clipboard!");
            } catch (clipboardErr) {
                alert("Failed to share.");
            }
        }
    }, [log]);


    return (
        <div className="flex flex-col gap-4 pb-20">
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col gap-4"
            >
                {/* Header Card */}
                <div className="glass-card relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-3xl rounded-full -mr-10 -mt-10"></div>

                    <div className="flex justify-between items-start mb-6 relative z-10">
                        <div className="text-[10px] uppercase tracking-widest text-text-muted font-bold mb-1">
                            {formatDateIndian(log.date)}
                        </div>
                        <div className="text-xs text-white flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full bg-white/10">
                                {log.workouts && log.workouts.length > 0 ? log.workouts[0].type : "Rest"}
                                {log.workouts && log.workouts.length > 1 && ` +${log.workouts.length - 1}`}
                            </span>
                            <span className="text-text-muted">•</span>
                            <span>{Math.round(totalCalories)} kcal In</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        {/* Score Circle */}
                        <div className="relative w-28 h-28 flex items-center justify-center">
                            <svg className="w-full h-full -rotate-90">
                                <circle
                                    cx="56"
                                    cy="56"
                                    r="50"
                                    className="stroke-white/5"
                                    strokeWidth="8"
                                    fill="none"
                                />
                                <motion.circle
                                    cx="56"
                                    cy="56"
                                    r="50"
                                    className="stroke-primary"
                                    strokeWidth="8"
                                    fill="none"
                                    strokeLinecap="round"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: displayScore / 100 }}
                                    transition={{ duration: 1.5, ease: "easeOut" }}
                                    strokeDasharray="1 1"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.5, type: "spring" }}
                                    className="text-4xl font-bold text-white tracking-tighter"
                                >
                                    {displayScore}
                                </motion.span>
                                <span className="text-[10px] uppercase tracking-wider text-text-muted font-medium">Score</span>
                            </div>
                        </div>

                        {/* Badges */}
                        <div className="flex-1 pl-6 flex flex-col gap-2">
                            {totalProtein > 120 && (
                                <motion.div
                                    initial={{ x: 20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-400/10 px-3 py-2 rounded-lg border border-emerald-400/20"
                                >
                                    <CheckCircle2 size={14} />
                                    <span className="font-medium">{Math.round(totalProtein)}g Protein</span>
                                </motion.div>
                            )}
                            {log.workouts && log.workouts.some(w => w.type !== "Rest") && (
                                <motion.div
                                    initial={{ x: 20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="flex items-center gap-2 text-xs text-blue-400 bg-blue-400/10 px-3 py-2 rounded-lg border border-blue-400/20"
                                >
                                    <Zap size={14} />
                                    <span className="font-medium">Workouts Done</span>
                                </motion.div>
                            )}
                            {analysis?.flags?.sleep_issue && (
                                <motion.div
                                    initial={{ x: 20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.4 }}
                                    className="flex items-center gap-2 text-xs text-rose-400 bg-rose-400/10 px-3 py-2 rounded-lg border border-rose-400/20"
                                >
                                    <AlertCircle size={14} />
                                    <span className="font-medium">Sleep {getSleepHours(log.sleep)}h</span>
                                </motion.div>
                            )}
                            {log.mood && (
                                <motion.div
                                    initial={{ x: 20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.5 }}
                                    className="flex items-center gap-2 text-xs text-violet-400 bg-violet-400/10 px-3 py-2 rounded-lg border border-violet-400/20"
                                >
                                    <Zap size={14} />
                                    <span className="font-medium">Mood: {log.mood}</span>
                                </motion.div>
                            )}
                        </div>
                    </div>


                    {/* Detail Grid */}
                    <div className="grid grid-cols-3 gap-2 mt-6 relative z-10">
                        {[
                            {
                                label: "Protein",
                                value: `${Math.round(totalProtein)}g`,
                                icon: Zap
                            },
                            {
                                label: "Sleep",
                                value: `${getSleepHours(log.sleep)}h`,
                                icon: Moon
                            },
                            {
                                label: "Burned",
                                value: `${localBurned}`,
                                icon: Flame
                            },
                            {
                                label: "Steps",
                                value: `${(log.steps || 0).toLocaleString()}`,
                                icon: Activity
                            },

                            {
                                label: "Intake",
                                value: `${Math.round(totalCalories)} kcal`,
                                icon: Utensils
                            },
                            {
                                label: "Mood",
                                value: log.mood || "-",
                                icon: Zap
                            },
                        ].map((stat, i) => (
                            <div key={i} className="bg-white/5 rounded-xl p-2 text-center border border-white/5">
                                <stat.icon size={12} className="mx-auto mb-1 text-text-muted" />
                                <div className="text-[9px] text-text-muted uppercase tracking-wider mb-0.5">{stat.label}</div>
                                <div className="text-xs font-bold text-white">{stat.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* AI Analysis Sections */}
                    <div className="mt-6 space-y-4">
                        {/* Coach Note */}
                        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-2 opacity-10">
                                <Info size={40} className="text-blue-400" />
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-blue-300 font-bold mb-1">Coach Note</div>
                            <p className="text-xs text-blue-100 leading-relaxed italic">
                                &quot;{displayAnalysis.weekly_context_comment}&quot;
                            </p>
                        </div>

                        {/* Analysis Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            {/* Positives */}
                            <div className="bg-green-500/5 border border-green-500/10 rounded-xl p-3">
                                <div className="text-[10px] uppercase tracking-wider text-green-400 font-bold mb-2 flex items-center gap-1">
                                    <Check size={10} /> Well Done
                                </div>
                                <ul className="space-y-1">
                                    {displayAnalysis.score_reasons?.positives?.map((reason, i) => (
                                        <li key={i} className="text-[10px] text-gray-300 flex items-start gap-1.5">
                                            <span className="mt-0.5 w-1 h-1 rounded-full bg-green-500 shrink-0" />
                                            {reason}
                                        </li>
                                    ))}
                                    {(!displayAnalysis.score_reasons?.positives || displayAnalysis.score_reasons.positives.length === 0) && (
                                        <li className="text-[10px] text-gray-500 italic">No specific highlights today.</li>
                                    )}
                                </ul>

                            </div>

                            {/* Negatives */}
                            <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-3">
                                <div className="text-[10px] uppercase tracking-wider text-red-400 font-bold mb-2 flex items-center gap-1">
                                    <Activity size={10} /> Improve
                                </div>
                                <ul className="space-y-1">
                                    {displayAnalysis.score_reasons?.negatives?.map((reason, i) => (
                                        <li key={i} className="text-[10px] text-gray-300 flex items-start gap-1.5">
                                            <span className="mt-0.5 w-1 h-1 rounded-full bg-red-500 shrink-0" />
                                            {reason}
                                        </li>
                                    ))}
                                    {(!displayAnalysis.score_reasons?.negatives || displayAnalysis.score_reasons.negatives.length === 0) && (
                                        <li className="text-[10px] text-gray-500 italic">Solid day! No major issues.</li>
                                    )}
                                </ul>

                            </div>
                        </div>

                        {/* Tomorrow's Plan */}
                        <div className="glass-card">
                            <div className="text-[10px] uppercase tracking-widest mb-3 text-text-muted font-bold">Tomorrow&apos;s Focus</div>
                            <div className="space-y-2">
                                {displayAnalysis.tomorrow_actions?.map((action, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs text-gray-300">
                                        <div className="w-4 h-4 rounded-full bg-white/5 flex items-center justify-center text-[9px] text-white shrink-0 mt-0.5">
                                            {i + 1}
                                        </div>
                                        <span className="leading-relaxed">{action}</span>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>

                {/* Workout & Other Details */}
                <div className="glass-card">
                    <div className="text-[10px] uppercase tracking-widest mb-4 text-text-muted font-bold flex items-center gap-2">
                        <Activity size={12} className="text-primary" /> Activity & Recovery
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="col-span-2 space-y-3">
                            {(log.workouts || []).map((w, i) => (
                                <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/5">
                                    <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider">Workout {i + 1}</div>
                                    <div className="text-xs font-bold text-white mb-1">{w.type || "Rest"}</div>
                                    <div className="text-[10px] text-text-muted">
                                        {w.duration || 0} mins • {w.calories || ((w.duration || 0) * 6) || 0} kcal
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="col-span-2 bg-white/5 rounded-xl p-3 border border-white/5">
                            <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider flex items-center gap-1">
                                <Droplets size={10} /> Water
                            </div>
                            <div className="text-xs font-bold text-white">{log.water} L</div>
                        </div>
                    </div>

                    <div>
                        <div className="text-[9px] text-text-muted mb-2 uppercase tracking-wider flex items-center gap-1">
                            <Pill size={10} /> Supplements
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {Object.entries(log.supplements || {}).filter(([, taken]) => taken).map(([name], i) => (
                                <span key={i} className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[10px] text-emerald-400 font-medium">
                                    {name}
                                </span>
                            ))}
                            {Object.values(log.supplements || {}).every(v => !v) && (
                                <span className="text-[10px] text-text-muted italic">None logged</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Lifestyle & Recovery */}
                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Sun size={18} className="text-secondary" />
                            <h3 className="text-lg font-bold text-white">
                                Lifestyle & Recovery
                            </h3>
                        </div>
                        {(() => {
                            const mod = calculateDailyScore(log) - calculateCoreScore(log);
                            const colorClass = mod > 0 ? "text-emerald-300 bg-emerald-500/20 border-emerald-500/30" :
                                mod < 0 ? "text-rose-300 bg-rose-500/20 border-rose-500/30" :
                                    "text-gray-300 bg-gray-500/20 border-gray-500/30";
                            return (
                                <span className={clsx("text-xs font-bold px-2.5 py-0.5 rounded-full border shadow-[0_0_10px_rgba(0,0,0,0.2)]", colorClass)}>
                                    {mod > 0 ? "+" : ""}{mod} pts
                                </span>
                            );
                        })()}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                            <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider flex items-center gap-1">
                                <AlertCircle size={10} /> Stress
                            </div>
                            <div className="text-xs font-bold text-white">{log.stress || 0}/5</div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                            <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider flex items-center gap-1">
                                <Smartphone size={10} /> Screen Time
                            </div>
                            <div className="text-xs font-bold text-white">{log.screenTime || 0}h</div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                            <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider flex items-center gap-1">
                                <Sun size={10} /> Sunlight
                            </div>
                            <div className="text-xs font-bold text-white">{log.sunlight || 0}m</div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                            <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider flex items-center gap-1">
                                <Users size={10} /> Social
                            </div>
                            <div className="text-xs font-bold text-white">{log.socialConnect || 0}/5</div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                            <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider flex items-center gap-1">
                                <Smile size={10} /> Mood
                            </div>
                            <div className="text-xs font-bold text-white truncate">{log.mood || "-"}</div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                            <div className="text-[9px] text-text-muted mb-1 uppercase tracking-wider flex items-center gap-1">
                                <Wine size={10} /> Alcohol
                            </div>
                            <div className="text-xs font-bold text-white">
                                {log.alcohol?.units || 0} drinks
                                {(log.alcohol?.units || 0) > 0 && log.alcohol?.type && (
                                    <span className="text-text-muted font-normal ml-1">({log.alcohol.type})</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/5">
                        <div className={clsx(
                            "p-2 rounded-lg",
                            log.smoking?.type === "No" ? "bg-emerald-500/10 text-emerald-400" :
                                log.smoking?.type === "Occasional" ? "bg-yellow-500/10 text-yellow-400" :
                                    "bg-red-500/10 text-red-400"
                        )}>
                            <Cigarette size={14} />
                        </div>
                        <div>
                            <div className="text-[9px] text-text-muted uppercase tracking-wider">Smoking Status</div>
                            <div className={clsx(
                                "text-xs font-bold",
                                log.smoking?.type === "No" ? "text-emerald-400" :
                                    log.smoking?.type === "Occasional" ? "text-yellow-400" :
                                        "text-red-400"
                            )}>
                                {log.smoking?.type || "No"}
                                {log.smoking?.type !== "No" && log.smoking?.quantity ? ` (${log.smoking.quantity})` : ""}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Meals Detail */}
                <div className="glass-card">
                    <div className="text-[10px] uppercase tracking-widest mb-4 text-text-muted font-bold flex items-center gap-2">
                        <Zap size={12} className="text-primary" /> Intake Details
                    </div>
                    <div className="space-y-4">
                        {(log.meals || []).map((meal, i) => {
                            const mealCalories = (meal.items || []).reduce((sum, item) => sum + (item.calories || 0) * (item.quantity || 1), 0);
                            const mealProtein = (meal.items || []).reduce((sum, item) => sum + (item.protein || 0) * (item.quantity || 1), 0);

                            return (
                                <div key={i} className="border-b border-white/5 last:border-0 pb-4 last:pb-0">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="text-xs font-bold text-white mb-1 flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                                                {meal.name}
                                            </div>
                                            <div className="text-[10px] text-text-muted pl-3.5">
                                                {Math.round(mealCalories)} kcal • <span className={mealProtein >= 30 ? "text-emerald-400 font-medium" : ""}>{Math.round(mealProtein)}g Protein</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5">
                                            {mealProtein >= 30 && (
                                                <div className="text-[9px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-medium flex items-center gap-1">
                                                    <Zap size={8} /> High Protein
                                                </div>
                                            )}
                                            {mealCalories > 800 && (
                                                <div className="text-[9px] bg-amber-500/10 border border-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-medium">
                                                    Heavy
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {(meal.items || []).length > 0 ? (
                                        <div className="space-y-2 pl-3.5">
                                            {meal.items.map((item, j) => (
                                                <div key={j} className="flex justify-between text-[10px] group">
                                                    <span className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                                        {item.quantity}x {item.name}
                                                    </span>
                                                    <span className="text-gray-500 font-mono">{item.calories * item.quantity} kcal</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-[10px] text-text-muted italic pl-3">No items logged</div>
                                    )}
                                </div>
                            )
                        })
                        }
                    </div>
                </div>


                <button
                    onClick={handleShare}
                    className="w-full bg-primary text-black font-bold uppercase tracking-widest text-xs py-4 rounded-xl shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                    <Share2 size={16} />
                    Share Day Summary
                </button>
            </motion.div >
        </div >
    );
}
